import React, { useMemo } from 'react';

import { visuallyHidden } from '@lumapps/classnames';
import { usePrevious } from '@lumapps/lumx-hooks/hooks/usePrevious';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { getMainWidget } from '../../ducks/selectors';
import { WidgetState } from '../../ducks/type';
import { getBlockResultsLength } from '../../utils/getBlockResultsLength';

interface LoadingBlockHelperProps {
    /** The container of your helper, default is p, but if you are in a ul, you want to use a li for example. */
    containerAs?: React.ElementType;
    /** The widget loading state */
    loadingState: WidgetState['state'];
}

export const LoadingBlockHelper = ({ containerAs: Component = 'p', loadingState }: LoadingBlockHelperProps) => {
    const { translateKey, translateAndReplace, pluralize } = useTranslate();
    const wasLoading = usePrevious(loadingState === 'loadingcontent');
    const { filters, body } = useSelector(getMainWidget) ?? {};

    const resultsLength = getBlockResultsLength(body);

    const message = useMemo(() => {
        /** Display loading message when loading initial data or more */
        if (loadingState === 'loadingcontent' || loadingState === 'loadingmore') {
            return translateKey(GLOBAL.LOADING);
        }

        /** Display loading results message after initial load or refresh (filters)
         * @warning we do not display the loading results for load more, it's already handled by the blocks
         * */
        if (loadingState === 'loaded' && wasLoading) {
            return pluralize(
                translateAndReplace(
                    filters?.searchQuery ? GLOBAL.NB_RESULTS_DISPLAYED_FOR_QUERY : GLOBAL.NB_RESULTS_DISPLAYED,
                    {
                        query: filters?.searchQuery,
                        NB: resultsLength,
                    },
                ),
                resultsLength,
            );
        }

        if (loadingState === 'error') {
            return translateKey(GLOBAL.ERROR);
        }
        return undefined;
    }, [loadingState, wasLoading, translateKey, pluralize, translateAndReplace, filters?.searchQuery, resultsLength]);

    return (
        <Component tabIndex={-1} className={visuallyHidden()} role="alert" aria-live="polite">
            {message}
        </Component>
    );
};
